import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PEOOrganizationService } from '@/services/peo-organization.service';
import { useToast } from '@/hooks/use-toast';
import { PEORoleType, ROLE_TYPE_LABELS } from '@/types/peo-organization.types';

interface OrgPositionDialogProps {
  providerId: string;
  positionId?: string;
  children: React.ReactNode;
}

export const OrgPositionDialog: React.FC<OrgPositionDialogProps> = ({
  providerId,
  positionId,
  children
}) => {
  const [open, setOpen] = useState(false);
  const [positionTitle, setPositionTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [roleType, setRoleType] = useState<PEORoleType>('sales_rep');
  const [description, setDescription] = useState('');
  const [isManagerRole, setIsManagerRole] = useState(false);
  const [reportsTo, setReportsTo] = useState<string>('');

  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get existing organizational roles for role type selection
  const { data: orgRoles } = useQuery({
    queryKey: ['organizational-roles', providerId],
    queryFn: () => PEOOrganizationService.getOrganizationalRoles(providerId),
    enabled: open && !!providerId
  });

  // Get existing org chart nodes for parent position selection
  const { data: orgNodes } = useQuery({
    queryKey: ['org-chart-nodes', providerId],
    queryFn: () => PEOOrganizationService.getOrgChartNodes(providerId),
    enabled: open && !!providerId
  });

  const createPosition = useMutation({
    mutationFn: async (data: {
      title: string;
      department: string;
      roleType: PEORoleType;
      description: string;
      isManager: boolean;
      parentId?: string;
    }) => {
      // For now, we'll create this as a team structure entry
      // In a real implementation, you might want a separate positions table
      return await PEOOrganizationService.createTeamStructure({
        provider_id: providerId,
        child_user_id: '', // This would need to be filled when assigning someone
        parent_user_id: data.parentId || null,
        team_name: data.title,
        department: data.department,
        region: null,
        hierarchy_level: 1, // This would be calculated based on hierarchy
        created_by: '', // This would be the current user
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-chart-nodes', providerId] });
      queryClient.invalidateQueries({ queryKey: ['team-structure', providerId] });
      toast({
        title: 'Success',
        description: 'Position created successfully',
      });
      setOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to create position: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setPositionTitle('');
    setDepartment('');
    setRoleType('sales_rep');
    setDescription('');
    setIsManagerRole(false);
    setReportsTo('');
  };

  const handleSubmit = () => {
    if (!positionTitle.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Position title is required',
        variant: 'destructive',
      });
      return;
    }

    createPosition.mutate({
      title: positionTitle,
      department: department,
      roleType: roleType,
      description: description,
      isManager: isManagerRole,
      parentId: (reportsTo && reportsTo !== 'none') ? reportsTo : undefined,
    });
  };

  // Flatten org nodes for parent selection
  const flattenNodes = (nodes: any[]): any[] => {
    const flattened: any[] = [];
    
    const traverse = (nodeList: any[], level = 0) => {
      nodeList.forEach(node => {
        flattened.push({ ...node, level });
        if (node.children) {
          traverse(node.children, level + 1);
        }
      });
    };
    
    traverse(nodes);
    return flattened;
  };

  const flatNodes = orgNodes ? flattenNodes(orgNodes) : [];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {positionId ? 'Edit Position' : 'Add New Position'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="position-title">Position Title *</Label>
              <Input
                id="position-title"
                value={positionTitle}
                onChange={(e) => setPositionTitle(e.target.value)}
                placeholder="e.g., Senior Sales Manager"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="e.g., Sales, Marketing, HR"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="role-type">Role Type</Label>
              <Select value={roleType} onValueChange={(value) => setRoleType(value as PEORoleType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reports-to">Reports To</Label>
              <Select value={reportsTo} onValueChange={setReportsTo}>
                <SelectTrigger>
                  <SelectValue placeholder="Select parent position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Parent (Top Level)</SelectItem>
                  {flatNodes.map((node) => (
                    <SelectItem key={node.id} value={node.id}>
                      {'  '.repeat(node.level)}{node.name} - {node.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the responsibilities and requirements for this position..."
              rows={3}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is-manager"
              checked={isManagerRole}
              onCheckedChange={setIsManagerRole}
            />
            <Label htmlFor="is-manager">This is a management position</Label>
          </div>

          {isManagerRole && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">
                This position will be marked as a management role and will have the ability to oversee direct reports.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={createPosition.isPending || !positionTitle.trim()}
          >
            {createPosition.isPending ? 'Creating...' : 'Create Position'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};